import { Component } from 'react';
import PropTypes from 'prop-types';
import autoBind from 'react-autobind';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import loadable from '@utils/loadable';
import { withRouter } from 'react-router-dom';

import TopicsSvc from '../../../services/TopicsSvc';
import LocationsSvc from '../../../services/dbServices/LocationsSvc';
import SectorsSvc from '../../../services/SectorsSvc';

import {
  addPathOnBreadcrumbs,
  removeSlugFromPath,
  removeSentimentAndSlugFromPath,
} from '../../../helpers/topicsHelpers';
import { setLocationMetadata, setSEOTopicMetadata } from '../../../helpers/metaDataHelpers';
import { getLocationParentData } from '../../../helpers/locationHelpers';
import { getTrendingBarAssetQuery } from '../../../helpers/watchlistHelperFunctions';
import { filterByQuery, resetTrendingFilters } from '../../../actions/storyFilters.action';

import withComponentName from '../../../decorators/withComponentName';

import {
  SEO_BREADCRUMBS,
  TOPIC_CLASS_BREADCRUMBS,
  LOCATION_BREADCRUMBS,
  SECTOR_BREADCRUMBS,
  SECTORS_PATH,
  LOCATIONS_PATH,
  SECTORS_PATH_ITEM,
  LOCATIONS_PATH_ITEM,
  DIRECTORIES_PATH,
} from '../../../data/directory/constants';
import {
  LARGE,
  DESKTOP,
  TABLET,
  MOBILE,
} from '../../../data/screenSizes';
import {
  TOPIC_CLASS,
  SECTOR,
  INDUSTRY,
  SUB_INDUSTRY,
  CATEGORY,
  CONTINENT,
  SUBCONTINENT,
  COUNTRY,
  STATE,
  CITY,
  AREA,
  PLACE,
  LOCATION_TYPES,
} from '../../../data/directory/topicTypes';
import { PUBLIC_COMPANIES, PRIVATE_COMPANIES } from '../../../data/directory/topic_classes/topicClassTypes';
import { TOPIC_SLUG_TYPES_TABS, TOPIC_SLUG_TABS, TOPIC_SLUG_DEFAULT_TABS } from '../../../data/directory/topic_classes/constants';
import { NEWS } from '../../../data/directory/topic_classes/tabPaths';

import Breadcrumbs from '../../Breadcrumbs';
import Preloader from '../../Preloader';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const TopicSlugHeader = loadable(() => import('./TopicSlugHeader'));
const TopicTabBodyCommon = loadable(() => import('../TopicTabBodyCommon'));
const TopicSlugBody = loadable(() => import('./TopicSlugBody'));

export class TopicSlug extends Component {
  constructor(props) {
    super(props);
    autoBind(this);

    const { topicSlugName } = props;

    this.state = this.getState();
    this.isSummary = topicSlugName.toLowerCase() === 'summary';
    this.mounted = true;

    this.ctaTimer = null;
    this.parents = [];
  }

  async componentDidMount() {
    await this.prepareData();
    window.addEventListener('resize', this.onResize, { passive: true });
    this.onResize();
  }

  async componentDidUpdate(prevProps) {
    const { searchBarActive, topicSlugName } = this.props;

    // do not load new data if searchbar state is changed.
    if (prevProps.searchBarActive !== searchBarActive
      && prevProps.topicSlugName === topicSlugName) {
      return;
    }
    if (prevProps.topicSlugName !== topicSlugName) {
      this.setState({
        ...this.getState(),
      });
      this.prepareData();
      this.onResize();
    }
  }

  componentWillUnmount() {
    this.resetData();
    this.mounted = false;
    if (this.ctaTimer) clearTimeout(this.ctaTimer);
    window.removeEventListener('resize', this.onResize);
  }

  onResize() {
    let mode;

    switch (true) {
      case window.innerWidth >= 1171:
        mode = LARGE;
        break;
      case window.innerWidth >= 769:
        mode = DESKTOP;
        break;
      case window.innerWidth >= 427:
        mode = TABLET;
        break;
      default:
        mode = MOBILE;
        break;
    }

    this.setState({
      mode,
    });
  }

  getState() {
    const {
      slugType, assetType, match, history, location, topicSlugName,
    } = this.props;
    const isLocationTypes = LOCATION_TYPES.includes(slugType);
    const hasSentimentAnalysisTab = [CONTINENT, SUBCONTINENT].includes(slugType);
    const continentType = CONTINENT === slugType && ['antarctica', 'oceania', 'testing'].includes(topicSlugName);
    const tabType = hasSentimentAnalysisTab ? 'LOCATION_TYPES' : '';
    const hasNewsTab = (
      this.isSummary && (assetType === PUBLIC_COMPANIES || assetType === PRIVATE_COMPANIES)
    ) || (hasSentimentAnalysisTab && !continentType);

    const { tabs: allowedTabs } = TOPIC_SLUG_TYPES_TABS
      .find(({ type }) => type === tabType) || TOPIC_SLUG_DEFAULT_TABS;

    const tabs = (
      TOPIC_SLUG_TABS
        .filter((tab) => allowedTabs.includes(tab.id))
        .map((tab) => ({ ...tab }))
    );

    const { page } = match.params;
    let activeTab = tabs.find((tab) => (
      tab.path === page
      || tab.path === location.hash.slice(1)
    ));
    if (!activeTab) {
      activeTab = tabs.find((tab) => tab.path === NEWS) || tabs[0];
      if (typeof window !== 'undefined' && hasNewsTab) {
        history.replace(`${page ? removeSlugFromPath(window.location.pathname) : window.location.pathname}/${NEWS}`);
      }
    }
    activeTab.active = true;

    return {
      assetClass: null,
      topic: null,
      breadcrumbs: [],
      mode: DESKTOP,
      tabs,
      hasTab: true,
      isLocationTypes,
      hasNewsTab,
      activeTabNumber: activeTab.id,
    };
  }

  async getLocationByType(type, locationGetFunction) {
    const { topicSlugName } = this.props;
    const topic = await locationGetFunction(topicSlugName);

    if (!topic) {
      window.location.href = '/404';
      return;
    }

    let breadcrumbs;

    ({ breadcrumbs, parents: this.parents } = getLocationParentData(topic));

    topic.path = `${LOCATIONS_PATH}/${type}/${topic.slug}`;
    breadcrumbs = LOCATION_BREADCRUMBS.concat(
      addPathOnBreadcrumbs(
        breadcrumbs,
        LOCATIONS_PATH_ITEM,
      ),
    );

    return {
      topic,
      breadcrumbs,
    };
  }

  async prepareData() {
    const {
      slugName,
      topicSlugName,
      slugType,
      assetType,
      filterByQuery: filterByQueryAction,
    } = this.props;

    let [
      assetClass,
      topic,
      sector,
      industry,
      subIndustry,
    ] = Array(5).fill(null);
    let breadcrumbs = [];

    switch (slugType) {
      case TOPIC_CLASS:
        topic = !this.isSummary ? await (
          TopicsSvc.getTopicBySlug(topicSlugName)
        ) : null;
        assetClass = this.isSummary ? (
          await TopicsSvc.getTopicClassBySlug(slugName)
        ) : (topic && topic.asset_class);

        if (
          ((this.isSummary && !assetClass) || (!this.isSummary && !topic))
          && `${DIRECTORIES_PATH}/${assetClass.slug}` !== removeSlugFromPath(window.location.pathname)
        ) {
          window.location.href = '/404';
          return;
        }

        breadcrumbs = TOPIC_CLASS_BREADCRUMBS.concat([
          {
            name: assetClass.title,
            path: slugName,
          },
        ]);
        if (
          !this.isSummary
          || (this.isSummary && assetType !== PRIVATE_COMPANIES && assetType !== PUBLIC_COMPANIES)
        ) {
          breadcrumbs.push({
            name: this.isSummary ? (
              topicSlugName.toLowerCase().replace(/^\w/, (l) => l.toUpperCase())
            ) : topic.name,
            path: `${slugName}/${topicSlugName}`,
          });
        }
        break;
      case SECTOR:
        topic = await SectorsSvc.getSector(topicSlugName);
        sector = topic;

        if (!topic) window.location.href = `${window.location.origin}/404`;

        breadcrumbs = SECTOR_BREADCRUMBS.concat([
          {
            name: topic.name,
            path: `${SECTORS_PATH_ITEM}/${topic.slug}`,
          },
        ]);
        break;
      case INDUSTRY:
        topic = await SectorsSvc.getIndustry(topicSlugName);
        if (!topic) window.location.href = `${window.location.origin}/404`;

        sector = topic.sector;

        topic.path = `${SECTORS_PATH}/${sector.slug}/${topic.slug}`;
        if (removeSlugFromPath(topic.path)
          !== removeSentimentAndSlugFromPath(window.location.pathname, topic.slug)) {
          window.location.href = `${window.location.origin}/404`;
        }

        breadcrumbs = SECTOR_BREADCRUMBS.concat(
          addPathOnBreadcrumbs(
            [
              {
                name: sector.name,
                path: sector.slug,
              },
              {
                name: topic.name,
                path: '#',
              },
            ],
            SECTORS_PATH_ITEM,
          ),
        );
        break;
      case SUB_INDUSTRY:
        topic = await SectorsSvc.getSubIndustry(topicSlugName);
        if (!topic) window.location.href = `${window.location.origin}/404`;

        industry = topic.industry;
        sector = industry.sector;

        topic.path = `${SECTORS_PATH}/${sector.slug}/${industry.slug}/${topic.slug}`;
        if (removeSlugFromPath(topic.path)
          !== removeSentimentAndSlugFromPath(window.location.pathname, topic.slug)) {
          window.location.href = `${window.location.origin}/404`;
        }

        breadcrumbs = SECTOR_BREADCRUMBS.concat(
          addPathOnBreadcrumbs(
            [
              {
                name: sector.name,
                path: `${sector.slug}`,
              },
              {
                name: industry.name,
                path: `${sector.slug}/${industry.slug}`,
              },
              {
                name: topic.name,
                path: '#',
              },
            ],
            SECTORS_PATH_ITEM,
          ),
        );
        break;
      case CATEGORY:
        topic = await SectorsSvc.getCategory(topicSlugName);
        if (!topic) window.location.href = `${window.location.origin}/404`;

        subIndustry = topic.subindustry;
        industry = subIndustry.industry;
        sector = industry.sector;

        topic.path = `${SECTORS_PATH}/${sector.slug}/${industry.slug}/${subIndustry.slug}/${topic.slug}`;
        if (removeSlugFromPath(topic.path)
          !== removeSentimentAndSlugFromPath(window.location.pathname, topic.slug)) {
          window.location.href = `${window.location.origin}/404`;
        }

        breadcrumbs = SEO_BREADCRUMBS.concat(
          addPathOnBreadcrumbs(
            [
              // TODO: show breadcrums or delete in case of redesign
              // {
              //   name: sector.name,
              //   path: sector.slug,
              // },
              // {
              //   name: industry.name,
              //   path: `${sector.slug}/${industry.slug}`,
              // },
              // {
              //   name: subIndustry.name,
              //   path: `${sector.slug}/${industry.slug}/${subIndustry.slug}`,
              // },
              {
                name: topic.name,
                path: '#',
              },
            ],
            SECTORS_PATH_ITEM,
          ),
        );
        break;
      case CONTINENT:
        ({ topic, breadcrumbs } = await this.getLocationByType('continent', LocationsSvc.getContinent));
        filterByQueryAction(getTrendingBarAssetQuery(topic, 'Continent'));
        break;
      case SUBCONTINENT:
        ({ topic, breadcrumbs } = await this.getLocationByType('subcontinent', LocationsSvc.getSubContinent));
        filterByQueryAction(getTrendingBarAssetQuery(topic, 'SubContinent'));
        break;
      case COUNTRY:
        ({ topic, breadcrumbs } = await this.getLocationByType('country', LocationsSvc.getCountry));
        filterByQueryAction(getTrendingBarAssetQuery(topic, 'Country'));
        break;
      case STATE:
        ({ topic, breadcrumbs } = await this.getLocationByType('state', LocationsSvc.getState));
        filterByQueryAction(getTrendingBarAssetQuery(topic, 'State'));
        break;
      case CITY:
        ({ topic, breadcrumbs } = await this.getLocationByType('city', LocationsSvc.getCity));
        filterByQueryAction(getTrendingBarAssetQuery(topic, 'City'));
        break;
      case AREA:
        ({ topic, breadcrumbs } = await this.getLocationByType('area', LocationsSvc.getArea));
        filterByQueryAction(getTrendingBarAssetQuery(topic, 'Area'));
        break;
      case PLACE:
        ({ topic, breadcrumbs } = await this.getLocationByType('place', LocationsSvc.getPlace));
        filterByQueryAction(getTrendingBarAssetQuery(topic, 'Place'));
        break;
      default:
    }

    if (this.mounted) {
      const hideSentimentAnalysisTab = SUBCONTINENT === slugType
        && ['antarctica', 'oceania', 'testing'].includes(topic?.parent?.continent?.slug);
      this.setState(({ hasNewsTab }) => ({
        assetClass,
        topic,
        breadcrumbs,
        hasNewsTab: hideSentimentAnalysisTab ? false : hasNewsTab,
      }));
      if (LOCATION_TYPES.includes(slugType)) setLocationMetadata(topic.name);
      else if (slugType !== TOPIC_CLASS) {
        setSEOTopicMetadata({
          topicName: topic.name,
        });
      }
    }
  }

  resetData() {
    const { resetTrendingFilters: resetTrendingFiltersAction } = this.props;
    resetTrendingFiltersAction();
  }

  scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    // eslint-disable-next-line func-names
    const onScroll = () => {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener('scroll', onScroll);
        callback();
      }
    };

    window.addEventListener('scroll', onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: 'smooth',
    });
  }

  changeTab(id, offset) {
    const { piwikEnabled } = this.props;
    if (piwikEnabled) {
      _paq.push(['trackEvent', 'Tabs', `Tab: ${id}`]);
    }
    if (!offset) {
      let { tabs } = this.state;
      const { history, match } = this.props;
      const { page } = match.params;
      tabs = tabs.map((tab) => ({
        ...tab,
        active: tab.id === id,
      }));

      const { path } = tabs.find((tab) => tab.active);
      if (page) {
        history.push(`${removeSlugFromPath(window.location.pathname)}/${path}`);
      } else {
        window.location.hash = path;
      }

      this.setState({ tabs, activeTabNumber: id });
      return;
    }
    this.scrollTo(offset, () => {
      this.changeTab(id, null);
    });
  }

  render() {
    const {
      topic,
      assetClass,
      breadcrumbs,
      mode,
      tabs,
      hasNewsTab,
      isLocationTypes,
      activeTabNumber,
    } = this.state;
    const {
      slugType,
      user,
      piwikEnabled,
      searchBarActive,
      isChatbotOpened,
      windowWidth,
    } = this.props;

    const isTabletViewWhileChatbotIsOpened = isChatbotOpened
    && (windowWidth > 1170 && windowWidth <= 1500);

    return (
      <article
        className={
          cx('topic', {
            'blured-background': searchBarActive,
          })
        }
      >
        <Preloader loading={!topic && !assetClass} static centered />
        {(topic || assetClass) && (
          <>
            <Breadcrumbs items={breadcrumbs} />
            <TopicSlugHeader
              mode={mode}
              topic={this.isSummary ? {
                ...assetClass,
                name: assetClass.title,
              } : topic}
              user={user}
              slugType={slugType}
              isSummary={this.isSummary}
              hasNewsTab={hasNewsTab}
              piwikEnabled={piwikEnabled}
            />
            {
              hasNewsTab ? (
                <TopicTabBodyCommon
                  isSummary={this.isSummary}
                  mode={mode}
                  slug={this.isSummary ? assetClass : topic}
                  slugType={slugType}
                  user={user}
                  piwikEnabled={piwikEnabled}
                  parents={this.parents}
                  tabs={tabs}
                  changeTab={this.changeTab}
                  hasNewsTab={hasNewsTab}
                  isLocationsTab={isLocationTypes}
                  activeTab={activeTabNumber}
                  isSEOPage
                  slugName={topic?.name.toLowerCase()}
                  isTabletViewWhileChatbotIsOpened={isTabletViewWhileChatbotIsOpened}
                />
              ) : (
                <TopicSlugBody
                  isSummary={this.isSummary}
                  mode={mode}
                  slug={this.isSummary ? assetClass : topic}
                  slugType={slugType}
                  user={user}
                  piwikEnabled={piwikEnabled}
                  parents={this.parents}
                  isLocationsTab={isLocationTypes}
                  isSEOPage
                  slugName={topic?.name?.toLowerCase()}
                  isTabletViewWhileChatbotIsOpened={isTabletViewWhileChatbotIsOpened}
                  test
                />
              )
            }
          </>
        )}
      </article>
    );
  }
}

TopicSlug.defaultProps = {
  slugName: '',
};

TopicSlug.propTypes = {
  slugType: PropTypes.oneOf([
    TOPIC_CLASS,
    SECTOR,
    INDUSTRY,
    SUB_INDUSTRY,
    CATEGORY,
    CONTINENT,
    SUBCONTINENT,
    COUNTRY,
    STATE,
    CITY,
    AREA,
    PLACE,
  ]).isRequired,
  slugName: PropTypes.string,
  topicSlugName: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  searchBarActive: state.searchReducers.searchBarActive,
  userToken: state.watchlistReducer.userToken,
  isChatbotOpened: state.chatbot.isChatbotOpened,
  windowWidth: state.common.width,
});

export default withComponentName(
  connect(mapStateToProps, { filterByQuery, resetTrendingFilters })(withRouter(TopicSlug)),
);
